import { format, isValid, parseISO, subDays } from 'date-fns';
import { CalendarIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/shared/lib/utils';

export interface DateRange {
  /** ISO date, yyyy-MM-dd. */
  from: string;
  to: string;
}

const PRESETS: ReadonlyArray<{ label: string; days: number }> = [
  { label: 'Last 7 days', days: 7 },
  { label: 'Last 30 days', days: 30 },
  { label: 'Last 90 days', days: 90 },
];

/** ISO `yyyy-MM-dd`, which is what the backend's Date filters expect. */
export function toIsoDate(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

export function lastNDays(days: number): DateRange {
  const today = new Date();
  return { from: toIsoDate(subDays(today, days)), to: toIsoDate(today) };
}

function parse(value: string): Date | undefined {
  const date = parseISO(value);
  return isValid(date) ? date : undefined;
}

/**
 * Date range filter.
 *
 * Presets first, because "last 30 days" covers the overwhelming majority of
 * operational queries and picking two dates by hand for it is needless work.
 * The calendar is there for the genuine "what happened on the 3rd" case (§44).
 *
 * The range is required rather than optional: it is the only filter the Orders
 * endpoint applies server-side, so an empty range would mean asking for every
 * order ever placed. See `orders.api.ts`.
 */
export function DateRangePicker({
  value,
  onChange,
  className,
}: {
  value: DateRange;
  onChange: (range: DateRange) => void;
  className?: string;
}) {
  const fromDate = parse(value.from);
  const toDate = parse(value.to);

  const label =
    fromDate && toDate
      ? `${format(fromDate, 'd MMM')} – ${format(toDate, 'd MMM yyyy')}`
      : 'Select dates';

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            'border-input text-foreground hover:border-input hover:text-foreground h-8 justify-start gap-2 text-xs font-normal',
            className,
          )}
        >
          <CalendarIcon className="size-3.5" aria-hidden />
          {label}
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-auto p-0" align="start">
        <div className="flex flex-col gap-1 p-2">
          {PRESETS.map((preset) => (
            <Button
              key={preset.days}
              variant="ghost"
              size="sm"
              className="h-7 justify-start text-xs"
              onClick={() => onChange(lastNDays(preset.days))}
            >
              {preset.label}
            </Button>
          ))}
        </div>

        <Separator />

        <Calendar
          mode="range"
          defaultMonth={fromDate}
          selected={{ from: fromDate, to: toDate }}
          onSelect={(range) => {
            // react-day-picker reports a partial range mid-selection; only
            // commit once both ends exist, or the table would refetch against
            // a half-chosen range.
            if (range?.from && range.to) {
              onChange({ from: toIsoDate(range.from), to: toIsoDate(range.to) });
            }
          }}
          numberOfMonths={1}
        />
      </PopoverContent>
    </Popover>
  );
}
